import type { AssetUrlLookup } from '../grid/useAssetUrls';
import type { OlaTokenLookup } from '../grid/useOlaTokens';
import { NameplateSection } from './NameplateSection';
import type { NameplateGroup } from './assetHierarchy';

interface HierarchicalAssetViewProps {
  nameplateGroups: NameplateGroup[];
  urls: AssetUrlLookup;
  olaTokens: OlaTokenLookup;
  isSelected: (id: string) => boolean;
  isSelectable?: (id: string) => boolean;
  onSelect: (id: string, modifiers: { shift?: boolean; meta?: boolean }) => void;
  onOpen: (id: string) => void;
  eligibleIds?: Set<string> | null;
  onToggleBasket?: (id: string) => void;
  basketIds?: Set<string>;
  expiredIds?: Set<string>;
  expiringSoonIds?: Set<string>;
  /**
   * Owned by the page, not this view: `orderedIds`/`totalVisible` need to
   * agree on which assets are actually on screen, so collapse state can't
   * live below the component that computes those.
   */
  collapsedNameplates: Set<string>;
  collapsedTypes: Set<string>;
  onToggleNameplateCollapse: (nameplateKey: string) => void;
  onToggleTypeCollapse: (collapseKey: string) => void;
}

export function HierarchicalAssetView({
  nameplateGroups,
  urls,
  olaTokens,
  isSelected,
  isSelectable,
  onSelect,
  onOpen,
  eligibleIds,
  onToggleBasket,
  basketIds,
  expiredIds,
  expiringSoonIds,
  collapsedNameplates,
  collapsedTypes,
  onToggleNameplateCollapse,
  onToggleTypeCollapse,
}: HierarchicalAssetViewProps) {
  if (nameplateGroups.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 pb-8">
      {nameplateGroups.map((group) => {
        const isCollapsed = collapsedNameplates.has(group.nameplate);
        return (
          <NameplateSection
            key={group.nameplate}
            nameplateGroup={group}
            isCollapsed={isCollapsed}
            onToggleCollapse={() => onToggleNameplateCollapse(group.nameplate)}
            collapsedTypes={collapsedTypes}
            onToggleTypeCollapse={onToggleTypeCollapse}
            urls={urls}
            olaTokens={olaTokens}
            isSelected={isSelected}
            isSelectable={isSelectable}
            onSelect={onSelect}
            onOpen={onOpen}
            eligibleIds={eligibleIds}
            onToggleBasket={onToggleBasket}
            basketIds={basketIds}
            expiredIds={expiredIds}
            expiringSoonIds={expiringSoonIds}
          />
        );
      })}
    </div>
  );
}
